/**
 * Plain-fetch client for the always-injected `AGENTS.md` documents exposed by
 * axilon-api-service at `/api/ontology/agent-memory/{scope}`. Mirrors the
 * agent-conversations client: the api-service is on a different base URL than
 * the proxy-backed ontology slices, so a thin fetch helper is simpler than RTK.
 */

import { createOntologyHeaders } from '../api/core'
import { getOntologyServiceBaseUrl } from '../config'

/**
 * `org` is the organization's shared document, readable by everyone and
 * rewritable only by an admin. `user` is the caller's own, private to them.
 */
export type AgentMemoryScope = 'org' | 'user'

export interface AgentMemoryDoc {
  content: string
  etag: string
  lastModified: string
}

/** GET result — `available: false` when `AGENT_MEMORY_BUCKET` is unset (503). */
export type AgentMemoryLoad =
  | { available: false }
  | { available: true; doc: AgentMemoryDoc }
  | { available: true; forbidden: true; message: string }

/** PUT result. Conflict carries the current server state so the UI can prompt a reload. */
export type AgentMemorySave =
  | { status: 'ok'; doc: AgentMemoryDoc }
  | { status: 'conflict'; currentEtag: string; currentExcerpt: string; currentTruncated: boolean }
  | { status: 'too_large' }
  | { status: 'forbidden'; message: string }
  | { status: 'unavailable' }

function endpoint(scope: AgentMemoryScope): string {
  return `${getOntologyServiceBaseUrl()}/agent-memory/${scope}`
}

async function forbiddenMessage(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as Partial<{ message: string }>
  return body.message || fallback
}

export async function getAgentMemory(
  scope: AgentMemoryScope,
  signal?: AbortSignal
): Promise<AgentMemoryLoad> {
  // Body-less GET: strip Content-Type so Fastify's JSON parser doesn't reject
  // the empty body (same gotcha as the agent-conversations client).
  const headers = createOntologyHeaders()
  headers.delete('Content-Type')
  const url = endpoint(scope)
  const res = await fetch(url, { method: 'GET', headers, signal })
  if (res.status === 503) return { available: false }
  if (res.status === 403) {
    return {
      available: true,
      forbidden: true,
      message: await forbiddenMessage(res, 'You do not have access to this document.')
    }
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`GET ${url} failed with HTTP ${res.status}${text ? `: ${text}` : ''}`)
  }
  return { available: true, doc: (await res.json()) as AgentMemoryDoc }
}

export async function updateAgentMemory(
  scope: AgentMemoryScope,
  content: string,
  etag: string,
  signal?: AbortSignal
): Promise<AgentMemorySave> {
  const url = endpoint(scope)
  const res = await fetch(url, {
    method: 'PUT',
    headers: createOntologyHeaders(),
    body: JSON.stringify({ content, etag }),
    signal
  })
  if (res.status === 503) return { status: 'unavailable' }
  if (res.status === 413) return { status: 'too_large' }
  if (res.status === 403) {
    return {
      status: 'forbidden',
      message: await forbiddenMessage(res, 'You do not have permission to save this document.')
    }
  }
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as Partial<{
      currentEtag: string
      currentExcerpt: string
      currentTruncated: boolean
    }>
    return {
      status: 'conflict',
      currentEtag: body.currentEtag ?? '',
      currentExcerpt: body.currentExcerpt ?? '',
      currentTruncated: body.currentTruncated ?? false
    }
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`PUT ${url} failed with HTTP ${res.status}${text ? `: ${text}` : ''}`)
  }
  return { status: 'ok', doc: (await res.json()) as AgentMemoryDoc }
}
